import { useState, useMemo } from "react";
import { VenueDetailsBySlugResponse, useAuth, useI18N } from "common/hooks";
import FloorNotesContent from "./FloorNotesContent";
import ToDoTaskContent from "./ToDoTaskContent";
import ProjectInformationContent from "./ProjectInformationContent";
import PublicContent from "./PublicContent";
import { backendUrl } from "common/config";
import { CaretRightIcon, CaretDownIcon, PlusCircleIcon } from "@phosphor-icons/react";
import { groupNotesByFloor, sortGroupedData } from "@/utils/mapperNotesHelpers";
import _ from "lodash";

type AccessibilityAlertsTabProps = {
  venueDetailsResponse: VenueDetailsBySlugResponse | undefined;
  onRefresh?: () => void;
  pageName?: string;
  initialTopTab?: "mapper" | "public";
};

type TopTabType = "mapper" | "public";
type MapperSubTabType = "floor-note" | "todo-task" | "project-information";

interface SubItem {
  id: string;
  title: string;
  count: number;
}

function AccessibilityAlertsTab({
  venueDetailsResponse,
  onRefresh,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  pageName = "Alerts Tab",
  initialTopTab,
}: AccessibilityAlertsTabProps) {
  const i18n = useI18N();
  const { isLoggedIn, user } = useAuth();

  const showMapperView =
    isLoggedIn &&
    (user?.roles?.isSociabilityMapper || user?.roles?.isSociabilityTeam);

  const [activeTopTab, setActiveTopTab] = useState<TopTabType>(
    initialTopTab ?? (showMapperView ? "mapper" : "public"),
  );
  const [activeMapperSubTab, setActiveMapperSubTab] =
    useState<MapperSubTabType>("floor-note");
  const [selectedSubItemId, setSelectedSubItemId] = useState<
    string | undefined
  >();
  const [scrollTrigger, setScrollTrigger] = useState(0);

  // Calculate note counts
  const noteCounts = useMemo(() => {
    if (!venueDetailsResponse) {
      return { floorNotes: 0, todoTasks: 0, projectInfo: 0, publicNotes: 0 };
    }

    const { businessNotesForMappers, enterpriseBusinessNotesActions } =
      venueDetailsResponse;
    const businessNotesAll = venueDetailsResponse?.details?.businessNotesAll;

    const floorActionIds =
      enterpriseBusinessNotesActions
        ?.filter((a) => a.children_business_notes_action.length === 0)
        .map((a) => a.id) || [];
    const floorNotesCount =
      businessNotesForMappers?.filter((n) =>
        floorActionIds.includes(n.business_notes_action_id),
      ).length || 0;

    const taskActionIds: number[] = [];
    enterpriseBusinessNotesActions?.forEach((action) => {
      action.children_business_notes_action?.forEach((child) => {
        if (child.show_tasks === 1) taskActionIds.push(child.id);
      });
    });
    const todoTasksCount =
      businessNotesForMappers?.filter((n) =>
        taskActionIds.includes(n.business_notes_action_id),
      ).length || 0;

    const projectInfoActionIds: number[] = [];
    enterpriseBusinessNotesActions?.forEach((action) => {
      action.children_business_notes_action?.forEach((child) => {
        if (child.show_tasks === 0) projectInfoActionIds.push(child.id);
      });
    });
    const projectInfoCount =
      businessNotesForMappers?.filter((n) =>
        projectInfoActionIds.includes(n.business_notes_action_id),
      ).length || 0;

    return {
      floorNotes: floorNotesCount,
      todoTasks: todoTasksCount,
      projectInfo: projectInfoCount,
      // Counts every public alert, matching the unscoped list in PublicContent.
      publicNotes: businessNotesAll?.length || 0,
    };
  }, [venueDetailsResponse]);

  // Compute accordion sub-items for each mapper parent tab
  const mapperAccordionItems = useMemo(() => {
    if (!venueDetailsResponse)
      return { floorItems: [], taskItems: [], projectItems: [] };

    const {
      businessNotesForMappers,
      enterpriseBusinessNotesActions,
      businessNotesTasks,
    } = venueDetailsResponse;

    // Floor sub-items: grouped by floor location
    const floorActionIds = (enterpriseBusinessNotesActions ?? [])
      .filter((a) => a.children_business_notes_action.length === 0)
      .map((a) => a.id);
    const floorNotes = (businessNotesForMappers ?? []).filter((n) =>
      floorActionIds.includes(n.business_notes_action_id),
    );
    const floorGroups = sortGroupedData(groupNotesByFloor(floorNotes));
    const floorItems: SubItem[] = floorGroups
      .map((group) => {
        const title =
          group.floor?.locationType === "Business"
            ? "Full site"
            : group.floor?.locationName ||
              group.floor?.categoryHierarchy ||
              "General";
        return {
          id: title.toLowerCase().replace(/\s+/g, "-"),
          title,
          count: group.notes.length,
        };
      })
      .filter((item) => item.count > 0);

    // Task sub-items: grouped by task type
    const taskActionIds: number[] = [];
    (enterpriseBusinessNotesActions ?? []).forEach((action) => {
      action.children_business_notes_action?.forEach((child) => {
        if (child.show_tasks === 1) taskActionIds.push(child.id);
      });
    });
    const taskNotes = (businessNotesForMappers ?? []).filter((n) =>
      taskActionIds.includes(n.business_notes_action_id),
    );
    const allTasks = [
      ...(businessNotesTasks?.General ?? []),
      ...(businessNotesTasks?.Custom ?? []),
    ];
    const taskGrouped = _.groupBy(taskNotes, "business_notes_task_id");
    const taskItems: SubItem[] = Object.entries(taskGrouped)
      .map(([taskId, notes]) => {
        const taskDetail = allTasks.find((t) => t.id === Number(taskId));
        return {
          id: taskId || "general",
          title: taskDetail?.task || "General Task",
          count: (notes as typeof taskNotes).length,
        };
      })
      .filter((item) => item.count > 0);

    // Project info sub-items: grouped by location
    const projectInfoActionIds: number[] = [];
    (enterpriseBusinessNotesActions ?? []).forEach((action) => {
      action.children_business_notes_action?.forEach((child) => {
        if (child.show_tasks === 0) projectInfoActionIds.push(child.id);
      });
    });
    const projectInfoNotes = (businessNotesForMappers ?? []).filter((n) =>
      projectInfoActionIds.includes(n.business_notes_action_id),
    );
    const projectGroups = sortGroupedData(groupNotesByFloor(projectInfoNotes));
    const projectItems: SubItem[] = projectGroups
      .map((group) => {
        const title =
          group.floor?.locationType === "Business"
            ? "Full site"
            : group.floor?.locationName ||
              group.floor?.categoryHierarchy ||
              "General";
        return {
          id: title.toLowerCase().replace(/\s+/g, "-"),
          title,
          count: group.notes.length,
        };
      })
      .filter((item) => item.count > 0);

    return { floorItems, taskItems, projectItems };
  }, [venueDetailsResponse]);

  const getSubItems = (tabId: MapperSubTabType): SubItem[] => {
    switch (tabId) {
      case "floor-note":
        return mapperAccordionItems.floorItems;
      case "todo-task":
        return mapperAccordionItems.taskItems;
      case "project-information":
        return mapperAccordionItems.projectItems;
    }
  };

  const handleParentTabClick = (tabId: MapperSubTabType) => {
    const subItems = getSubItems(tabId);
    setActiveMapperSubTab(tabId);
    // Auto-select first sub-item
    if (subItems.length > 0) {
      setSelectedSubItemId(subItems[0].id);
      setScrollTrigger((t) => t + 1);
    } else {
      setSelectedSubItemId(undefined);
    }
  };

  const handleSubItemClick = (
    parentTabId: MapperSubTabType,
    subItemId: string,
  ) => {
    setActiveMapperSubTab(parentTabId);
    setSelectedSubItemId(subItemId);
    setScrollTrigger((t) => t + 1);
  };

  const mapperSubTabs: { id: MapperSubTabType; label: string }[] = [
    {
      id: "floor-note",
      label: `${i18n.get("floorNote")} (${noteCounts.floorNotes})`,
    },
    {
      id: "todo-task",
      label: `${i18n.get("todoTask")} (${noteCounts.todoTasks})`,
    },
    {
      id: "project-information",
      label: `${i18n.get("projectInformation")} (${noteCounts.projectInfo})`,
    },
  ];

  const renderMapperSubContent = () => {
    switch (activeMapperSubTab) {
      case "floor-note":
        return (
          <FloorNotesContent
            venueDetailsResponse={venueDetailsResponse}
            onRefresh={onRefresh}
            hideSidebar
            scrollToId={selectedSubItemId}
            scrollTrigger={scrollTrigger}
          />
        );
      case "todo-task":
        return (
          <ToDoTaskContent
            venueDetailsResponse={venueDetailsResponse}
            onRefresh={onRefresh}
            hideSidebar
            scrollToId={selectedSubItemId}
            scrollTrigger={scrollTrigger}
          />
        );
      case "project-information":
        return (
          <ProjectInformationContent
            venueDetailsResponse={venueDetailsResponse}
            onRefresh={onRefresh}
            hideSidebar
            scrollToId={selectedSubItemId}
            scrollTrigger={scrollTrigger}
          />
        );
    }
  };

  const tabButtonClass = (isActive: boolean) =>
    `px-3 py-2 rounded-md focus-blue label-m-medium ${
      isActive
        ? "text-fg-brand-strong bg-container-brand-minimal"
        : "text-fg-neutral-subtle hover:bg-container-neutral-minimal"
    }`;

  // Sub-items for currently active parent (used on mobile)
  const activeSubItems = getSubItems(activeMapperSubTab);

  return (
    <div className="h-screen flex flex-col">
      {/* Top Tabs â€” Mapper / Public (only for mapper/team users) */}
      {showMapperView && (
        <div
          className="bg-bg-brand-secondary-default border-b border-border-token-neutral-minimal flex-shrink-0"
          role="tablist"
          aria-label="Alert view"
        >
          <div className="flex items-center gap-4 px-4 py-3 md:px-6 lg:px-8">
            <button
              role="tab"
              aria-selected={activeTopTab === "mapper"}
              aria-controls="mapper-tabpanel"
              id="mapper-tab"
              onClick={() => {
                setActiveTopTab("mapper");
              }}
              className={tabButtonClass(activeTopTab === "mapper")}
            >
              Mapper ({noteCounts.floorNotes + noteCounts.todoTasks + noteCounts.projectInfo})
            </button>

            <button
              role="tab"
              aria-selected={activeTopTab === "public"}
              aria-controls="public-tabpanel"
              id="public-tab"
              onClick={() => {
                setActiveTopTab("public");
              }}
              className={tabButtonClass(activeTopTab === "public")}
            >
              {i18n.get("public")} ({noteCounts.publicNotes})
            </button>

            {/* Add alert button â€” always visible */}
            <button
              type="button"
              onClick={() => {
                window.open(
                  `${backendUrl}/business/edit/${venueDetailsResponse?.details?.business?.id}?active_tab=accessibility&md=crtalert`,
                  "_blank",
                );
              }}
              className="ml-auto flex items-center gap-1.5 px-3 py-2 label-m-medium border border-border-token-brand-minimal text-fg-neutral-subtle hover:bg-container-neutral-minimal rounded-md transition-colors focus-blue"
            >
              <PlusCircleIcon size={16} aria-hidden="true" className="text-fg-neutral-strong" />
              Add alert
            </button>
          </div>
        </div>
      )}

      {/* Mapper tab panel */}
      {activeTopTab === "mapper" && showMapperView && (
        <div
          id="mapper-tabpanel"
          role="tabpanel"
          aria-labelledby="mapper-tab"
          className="flex flex-1 overflow-hidden"
        >
          {/* Desktop: single accordion sidebar */}
          <aside
            className="hidden md:flex w-64 lg:w-72 bg-bg-brand-secondary-default border-r border-border-token-neutral-minimal flex-shrink-0 py-6 overflow-y-auto"
            aria-label="Mapper note type"
          >
            <nav className="w-full px-4">
              <ul>
                {mapperSubTabs.map((tab) => {
                  const isActive = activeMapperSubTab === tab.id;
                  const subItems = getSubItems(tab.id);

                  return (
                    <li key={tab.id}>
                      {/* Parent row */}
                      <button
                        type="button"
                        aria-expanded={isActive}
                        onClick={() => handleParentTabClick(tab.id)}
                        className={`w-full flex items-center justify-between pl-4 pr-2 py-3 relative transition-colors focus-blue ${
                          isActive
                            ? "text-fg-brand-subtle"
                            : "text-fg-neutral-strong hover:text-fg-brand-subtle"
                        }`}
                      >
                        {/* Guide line scoped to this button's height only */}
                        <div className="absolute left-0 top-0 bottom-0 w-[2px] bg-border-token-brand-minimal" />
                        {isActive && (
                          <div className="absolute left-0 top-0 bottom-0 w-[2px] bg-bg-brand-primary-default" />
                        )}
                        <span className="label-m-medium">{tab.label}</span>
                        {isActive ? (
                          <CaretDownIcon
                            size={16}
                            weight="bold"
                            aria-hidden="true"
                            className="text-fg-brand-subtle flex-shrink-0"
                          />
                        ) : (
                          <CaretRightIcon
                            size={16}
                            weight="bold"
                            aria-hidden="true"
                            className="flex-shrink-0"
                          />
                        )}
                      </button>

                      {/* Sub-items (shown when parent is active) */}
                      {isActive && subItems.length > 0 && (
                        <ul className="relative mb-1">
                          {/* Guide line scoped to sub-items, indented to align with parent text */}
                          <div className="absolute left-4 top-0 bottom-0 w-[2px] bg-border-token-brand-minimal" />

                          {subItems.map((item) => {
                            const isSubActive = selectedSubItemId === item.id;
                            return (
                              <li key={item.id}>
                                <button
                                  type="button"
                                  aria-current={
                                    isSubActive ? "true" : undefined
                                  }
                                  onClick={() =>
                                    handleSubItemClick(tab.id, item.id)
                                  }
                                  className={`w-full flex items-center justify-between pl-8 pr-2 py-3 relative transition-colors focus-blue ${
                                    isSubActive
                                      ? "text-fg-brand-subtle"
                                      : "text-fg-neutral-strong hover:text-fg-brand-subtle"
                                  }`}
                                >
                                  <div className="absolute left-4 top-0 bottom-0 w-[2px] bg-border-token-brand-minimal" />
                                  {isSubActive && (
                                    <div className="absolute left-4 top-0 bottom-0 w-[2px] bg-bg-brand-primary-default" />
                                  )}
                                  <span className="label-m-medium text-left">
                                    {item.title}
                                  </span>
                                  <CaretRightIcon
                                    size={16}
                                    weight="bold"
                                    aria-hidden="true"
                                    className={`flex-shrink-0 ${isSubActive ? "text-fg-brand-subtle" : "text-fg-neutral-subtle"}`}
                                  />
                                </button>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </li>
                  );
                })}
              </ul>
            </nav>
          </aside>

          {/* Mobile: parent pills + optional sub-item pills */}
          <div className="flex flex-col flex-1 min-h-0 overflow-hidden">
            <div className="md:hidden flex-shrink-0">
              {/* Parent-level pills */}
              <div className="flex gap-2 px-4 py-3 border-b border-border-token-neutral-minimal overflow-x-auto">
                {mapperSubTabs.map((tab) => {
                  const isActive = activeMapperSubTab === tab.id;
                  return (
                    <button
                      key={tab.id}
                      type="button"
                      onClick={() => handleParentTabClick(tab.id)}
                      className={`px-3 py-1.5 rounded-full label-s-medium whitespace-nowrap focus-blue ${
                        isActive
                          ? "bg-container-brand-minimal text-fg-brand-strong"
                          : "bg-bg-brand-secondary-default border border-border-token-neutral-minimal text-fg-neutral-strong"
                      }`}
                    >
                      {tab.label}
                    </button>
                  );
                })}
              </div>

              {/* Sub-item pills (only when active parent has sub-items) */}
              {activeSubItems.length > 0 && (
                <div className="flex gap-2 px-4 py-2 border-b border-border-token-neutral-minimal bg-bg-brand-secondary-default overflow-x-auto">
                  {activeSubItems.map((item) => {
                    const isActive = selectedSubItemId === item.id;
                    return (
                      <button
                        key={item.id}
                        type="button"
                        onClick={() =>
                          handleSubItemClick(activeMapperSubTab, item.id)
                        }
                        className={`px-2.5 py-1 rounded-full label-xs-medium whitespace-nowrap focus-blue ${
                          isActive
                            ? "bg-container-brand-minimal text-fg-brand-strong"
                            : "bg-bg-brand-secondary-default border border-border-token-neutral-minimal text-fg-neutral-subtle"
                        }`}
                      >
                        {item.title}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Sub-content */}
            <div className="flex-1 min-h-0 overflow-hidden flex">
              {renderMapperSubContent()}
            </div>
          </div>
        </div>
      )}

      {/* Public tab panel */}
      {(activeTopTab === "public" || !showMapperView) && (
        <div
          id="public-tabpanel"
          role="tabpanel"
          aria-labelledby="public-tab"
          className="flex-1 min-h-0 overflow-hidden flex flex-col"
        >
          <PublicContent venueDetailsResponse={venueDetailsResponse} />
        </div>
      )}
    </div>
  );
}

export default AccessibilityAlertsTab;
